import React from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import LoginUserIcon from "../../assets/icons/LoginUserIcon";
import OrganizationIcon from "../../assets/icons/OrganizationIcon";
import FormsIcon from "../../assets/icons/FormsIcon";
import SettingsIcon from "../../assets/icons/SettingsIcon";
import TickIcon from "../../assets/icons/TickIcon";
import { logOut } from "../auth/services/auth";
import BuildingIcon from "../../assets/icons/BuildingIcon";
import LogoutIcon from "../../assets/icons/LogoutIcon";
import CreateFormBackground from "../../assets/images/CreateFormBackground.png";
interface ProfileDropdownProps {
  name?: string;
  email?: string;
  imageUrl?: string;
  userId?: number;
  organizationId?: number;
  onMenuItemClick?: () => void;
  /** When false, hide navigation menu items and only show header + sign out. */
  showMenuItems?: boolean;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  name = "Alex Johnson",
  email = "johnsonalex34@gmail.com",
  imageUrl,
  userId,
  organizationId,
  onMenuItemClick,
  showMenuItems = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const activePage = (searchParams.get("page") || "").toLowerCase();
  const isContactDetailsActive = activePage === "contactdetails";
  const isOrganizationDetailsActive = activePage === "organizationdetails";
  const isSettingsActive = activePage === "settings";

  const navigateToPreviewPage = (page: string, id?: string) => {
    const params = new URLSearchParams(location.search);
    params.set("page", page);
    params.delete("edit");

    if (id) {
      params.set("id", id);
    } else {
      params.delete("id");
    }

    const nextSearch = params.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  return (
    <CardWrapper>
      <ProfileHeader>
        <AvatarLarge>
          {imageUrl ? (
            <AvatarImage src={imageUrl} alt={name} />
          ) : (
            <AvatarPlaceholder>
              <LoginUserIcon size={40} color="#1e293b" />
            </AvatarPlaceholder>
          )}
        </AvatarLarge>
        <HeaderText>
          <HeaderName>{name}</HeaderName>
          <HeaderEmail>{email}</HeaderEmail>
        </HeaderText>
      </ProfileHeader>

      <Divider />
      {showMenuItems && (
        <MenuList>
          <MenuItem $active={isContactDetailsActive} onClick={() => {
            if (!userId) {
              return;
            }

            navigateToPreviewPage("contactDetails", encodeURIComponent(String(userId)));
            onMenuItemClick?.();
          }} >
            {/* <IconWrap> */}
            <LoginUserIcon size={18} color="#1E1E1E" />
            {/* </IconWrap> */}
            <ItemText>View Contact Details</ItemText>
            {isContactDetailsActive && (
              <ActiveTick>
                <TickIcon size={14} color="#0f4680" />
              </ActiveTick>
            )}
          </MenuItem>
          <MenuItem $active={isOrganizationDetailsActive} onClick={() => {
            if (!organizationId) {
              return;
            }

            navigateToPreviewPage("organizationDetails", encodeURIComponent(String(organizationId)));
            onMenuItemClick?.();
          }}>
            {/* <IconWrap> */}
            <BuildingIcon size={18} color="#1E1E1E" />
            {/* </IconWrap> */}
            <ItemText>Manage Organization</ItemText>
            {isOrganizationDetailsActive && (
              <ActiveTick>
                <TickIcon size={14} color="#0f4680" />
              </ActiveTick>
            )}
          </MenuItem>
          <MenuItem onClick={() => onMenuItemClick?.()}>
            {/* <IconWrap> */}
            <FormsIcon size={18} color="#1E1E1E" />
            {/* </IconWrap> */}
            <ItemText>Forms</ItemText>
          </MenuItem>
          <MenuItem $active={isSettingsActive} onClick={() => {
            navigateToPreviewPage("settings");
            onMenuItemClick?.();
          }} >
            {/* <IconWrap> */}
            <SettingsIcon size={18} color="#1E1E1E" />
            {/* </IconWrap> */}
            <ItemText>Account Settings</ItemText>
            {isSettingsActive && (
              <ActiveTick>
                <TickIcon size={14} color="#0f4680" />
              </ActiveTick>
            )}
          </MenuItem>
        </MenuList>
      )}

      <SignOutButton
        type="button"
        onClick={() => {
          void logOut();
        }}
      >
        <LogoutIcon />
        Sign out
      </SignOutButton>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  position: absolute;
  top: 62px;
  right: 0;
  width: 290px;
  background: #ffffff;
  background-image : url(${CreateFormBackground});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-position-y: -31px;
  border-radius: 0px 0px 8px 8px;
  box-shadow: 0 25px 60px rgba(15, 59, 112, 0.15);
   padding: 12px;
  z-index: 1100;
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
`;

const AvatarLarge = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dbeafe, #eff6ff);
  border: 4px solid #ffffff;
  box-shadow: 0 0 0 4px rgba(15, 59, 112, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const AvatarPlaceholder = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  margin-top: 12px;
`;

const HeaderName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #003366;
`;

const HeaderEmail = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(148, 163, 184, 0.35);
  margin: 18px 0;
`;

const MenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.li<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: ${({ $active }) => ($active ? "#0f4680" : "#374151")};
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
padding: 10px;
  &:hover {
    background-color: #f3f4f6;
    
  }
`;

const IconWrap = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #eef2ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ItemText = styled.span``;

const ActiveTick = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
`;

const SignOutButton = styled.button`
  margin-top: 20px;
  width: 100%;
  border-radius: 8px;
  border: none;
  padding: 12px 18px;
  background: #003366;
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  gap: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default ProfileDropdown;
